// Owned by task 2.5 of the dashboard-redesign spec (Requirement 9).
//
// Reads upcoming compliance items (owned by the `building-compliance` spec)
// and exposes them as `WidgetResult<UpcomingComplianceRow[]>` for the
// `Widget_UpcomingCompliance` list widget to consume. This hook is
// READ-ONLY.
//
// Fetch contract (see design.md §Low-Level Design §Fetch contract):
//   - Starts in `{ status: "loading" }`.
//   - On mount and every `scope` / `days` change: an `AbortController`-
//     guarded GET against
//     `/building-compliance/upcoming?days={n}&buildingId={id|ALL}`.
//   - The endpoint returns an array of `ComplianceRegister`-shaped items
//     and does NOT return `severity` — the hook derives it per Req 9.2 from
//     `daysUntilExpiry`. Any item whose `daysUntilExpiry > 90` is filtered
//     out defensively (should not happen if the endpoint respects `days`,
//     but keeps the SOON bucket cap explicit — see Req 9.2 boundary at 90).
//   - Rows are sorted ascending by `daysUntilExpiry` so `expired` items
//     (negative days) appear first, then `urgent`, then `soon` — per
//     Req 9.1 ("sorted ascending by days-until-expiry").
//   - Empty state when the array is empty (either the endpoint returned
//     zero rows, or every returned row was filtered by the defensive
//     `> 90` cap).
//   - Error state carries an `errorId` (from the `X-Error-Id` response
//     header if present, else a locally-generated UUID v4) so the scoped
//     error card can render it verbatim for support (Requirement 14).
//   - `retry` re-invokes the fetch by bumping a retry counter.
//   - On unmount: aborts the in-flight fetch.
//
// The `api` client (frontend/src/services/api.ts) already attaches the
// Clerk JWT and the `x-clerk-subdomain` tenant header via interceptors —
// this hook MUST NOT re-implement any of that.
//
// Coordination note: tasks 2.1 (`useDashboardArrears`) and 2.2
// (`useCollectionRate`) share this fetch pattern; the small helpers below
// are duplicated inline on purpose. A later refactor task will consolidate
// them into a `useWidgetFetch` primitive after all Wave 1 hooks have
// landed.

import { useCallback, useEffect, useState } from "react";
import type { AxiosError } from "axios";
import api from "../../../services/api";
import type {
  BuildingScope,
  ComplianceItem,
  UpcomingComplianceRow,
  WidgetResult,
} from "../types";

/** User-safe message shown in the scoped error card. Never leaks server text. */
const USER_SAFE_ERROR_MESSAGE = "Could not load upcoming compliance";

/** Default lookahead window per the task signature. */
const DEFAULT_DAYS = 90;

/** Milliseconds in one whole day. */
const MS_PER_DAY = 86_400_000;

/**
 * The SOON severity cap per Requirement 9.2. Items whose derived
 * `daysUntilExpiry` exceeds this are filtered out defensively — they would
 * fall outside the {expired, urgent, soon} enumeration and should not be
 * returned by the endpoint in the first place.
 */
const SOON_CAP_DAYS = 90;

/** Boundary: `daysUntilExpiry <= URGENT_CAP_DAYS` (and >= 0) → urgent. */
const URGENT_CAP_DAYS = 30;

/**
 * Generate a UUID v4. Prefers `globalThis.crypto.randomUUID` when
 * available; falls back to a small `Math.random`-based hex formatter for
 * environments that don't expose the WebCrypto API (older test runners,
 * JSDOM shims, etc.). Never used to authenticate anything — only to
 * correlate a UI error card to a log entry when the server didn't return
 * an `X-Error-Id` header.
 */
function generateLocalErrorId(): string {
  const cryptoRef = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
    return cryptoRef.randomUUID();
  }
  // RFC 4122 v4 shape: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx where
  // y ∈ {8, 9, a, b}.
  const rand = (): number => Math.floor(Math.random() * 16);
  const hex = (n: number): string => n.toString(16);
  let out = "";
  for (let i = 0; i < 36; i += 1) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      out += "-";
    } else if (i === 14) {
      out += "4";
    } else if (i === 19) {
      out += hex((rand() & 0x3) | 0x8);
    } else {
      out += hex(rand());
    }
  }
  return out;
}

/**
 * Extract an `X-Error-Id` header from an axios error's response.
 * Case-insensitive per RFC 7230; different transports normalise headers
 * differently (Node http lowercases everything, JSDOM leaves them as-is).
 */
function extractErrorIdFromAxios(err: unknown): string | undefined {
  if (!err || typeof err !== "object") return undefined;
  const response = (err as AxiosError).response;
  if (!response || !response.headers) return undefined;
  const headers = response.headers as
    | Record<string, string | undefined>
    | { get?: (key: string) => string | null };
  // Some axios versions expose headers as an AxiosHeaders instance with a
  // `get(key)` method; others expose a plain record. Handle both.
  if (typeof (headers as { get?: unknown }).get === "function") {
    const raw = (headers as { get: (key: string) => string | null }).get(
      "x-error-id",
    );
    if (typeof raw === "string" && raw.length > 0) return raw;
  }
  const record = headers as Record<string, string | undefined>;
  const candidate =
    record["x-error-id"] ??
    record["X-Error-Id"] ??
    record["X-ERROR-ID"];
  return typeof candidate === "string" && candidate.length > 0
    ? candidate
    : undefined;
}

/**
 * Validate that an unknown response body is an array of compliance items
 * with the fields this hook needs. Returns the typed array on success or
 * `undefined` on any structural mismatch — the caller then transitions to
 * the error state per the fetch contract.
 *
 * The hook only reads `id` and `nextDue` directly; every other
 * `ComplianceRegister` field is passed through untouched to the widget.
 * We validate the presence and parseability of `nextDue` here so a
 * malformed row can't crash the `Date.parse`-based severity derivation.
 */
function parseComplianceArray(body: unknown): ComplianceItem[] | undefined {
  if (!Array.isArray(body)) return undefined;
  const items: ComplianceItem[] = [];
  for (const raw of body) {
    if (!raw || typeof raw !== "object") return undefined;
    const r = raw as Record<string, unknown>;
    if (typeof r.id !== "string" || r.id.length === 0) return undefined;
    if (typeof r.nextDue !== "string" || r.nextDue.length === 0) {
      return undefined;
    }
    if (Number.isNaN(Date.parse(r.nextDue))) return undefined;
    items.push(raw as ComplianceItem);
  }
  return items;
}

/**
 * Derive severity per Requirement 9.2:
 *   - `daysUntilExpiry < 0` → "expired"
 *   - `0 <= daysUntilExpiry <= 30` → "urgent"
 *   - `30 < daysUntilExpiry <= 90` → "soon"
 *
 * The `> 90` filter is applied by the caller (not here) so this function
 * always returns one of the three enumerated severities.
 */
function severityFor(
  daysUntilExpiry: number,
): UpcomingComplianceRow["severity"] {
  if (daysUntilExpiry < 0) return "expired";
  if (daysUntilExpiry <= URGENT_CAP_DAYS) return "urgent";
  return "soon";
}

export function useUpcomingCompliance(
  scope: BuildingScope,
  days: number = DEFAULT_DAYS,
): WidgetResult<UpcomingComplianceRow[]> {
  const [retryCounter, setRetryCounter] = useState(0);
  const [result, setResult] = useState<WidgetResult<UpcomingComplianceRow[]>>({
    status: "loading",
  });

  const retry = useCallback(() => {
    setRetryCounter((n) => n + 1);
  }, []);

  const scopeKind = scope.kind;
  const scopeBuildingId = scope.kind === "single" ? scope.buildingId : null;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    // Reset to loading on scope/days/retry change so consumers see the
    // skeleton between fetches instead of stale ready data.
    setResult({ status: "loading" });

    const buildingId =
      scopeKind === "all" ? "ALL" : (scopeBuildingId ?? "ALL");
    const url =
      `/building-compliance/upcoming` +
      `?days=${encodeURIComponent(String(days))}` +
      `&buildingId=${encodeURIComponent(buildingId)}`;

    api
      .get(url, { signal: controller.signal })
      .then((response) => {
        if (cancelled) return;
        const items = parseComplianceArray(response?.data);
        if (!items) {
          setResult({
            status: "error",
            errorId: generateLocalErrorId(),
            message: "Unexpected response shape",
            retry,
          });
          return;
        }

        if (items.length === 0) {
          setResult({ status: "empty" });
          return;
        }

        const now = Date.now();
        const rows: UpcomingComplianceRow[] = [];
        for (const item of items) {
          const daysUntilExpiry = Math.floor(
            (Date.parse(item.nextDue) - now) / MS_PER_DAY,
          );
          // Defensive filter — the endpoint should already respect the
          // `days` query, but keep the SOON bucket cap enforced client-
          // side so malformed data cannot produce a row without a
          // severity classification.
          if (daysUntilExpiry > SOON_CAP_DAYS) continue;
          rows.push({
            item,
            daysUntilExpiry,
            severity: severityFor(daysUntilExpiry),
          });
        }

        if (rows.length === 0) {
          setResult({ status: "empty" });
          return;
        }

        // Ascending sort: most-negative (already expired) first, then
        // urgent, then soon. Ties by daysUntilExpiry keep insertion order,
        // which is stable in every modern JS engine.
        rows.sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);

        setResult({ status: "ready", data: rows });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // The browser/axios raises `CanceledError` (or a `DOMException`
        // named `AbortError`) when the AbortController fires. That path is
        // the caller's intent, not a failure — swallow silently.
        if (controller.signal.aborted) return;
        const errorId =
          extractErrorIdFromAxios(err) ?? generateLocalErrorId();
        setResult({
          status: "error",
          errorId,
          message: USER_SAFE_ERROR_MESSAGE,
          retry,
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `scope.kind`, `scope.buildingId`, and `days` are the only inputs;
    // `retryCounter` triggers a re-fetch on `retry()`. `retry` and `api`
    // are stable refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKind, scopeBuildingId, days, retryCounter]);

  return result;
}
